import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Moon, Palette, Sun } from 'lucide-react';
import { useTheme, type Theme } from '@/app/providers/theme-context';
import { cn } from '@/shared/lib/cn';

const THEME_OPTIONS: ReadonlyArray<{
  value: Theme;
  label: string;
  icon: typeof Sun;
}> = [
  { value: 'light', label: 'Claro', icon: Sun },
  { value: 'dark-blue', label: 'Dark blue', icon: Moon },
  { value: 'dark-purple', label: 'Dark purple', icon: Palette },
];

interface ThemeMenuButtonProps {
  containerClassName?: string;
  className?: string;
  menuClassName?: string;
}

export function ThemeMenuButton({ containerClassName, className, menuClassName }: ThemeMenuButtonProps) {
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 84 });
  const current = THEME_OPTIONS.find((option) => option.value === theme) ?? THEME_OPTIONS[0]!;
  const CurrentIcon = current.icon;

  const placeMenu = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const menuWidth = menuRef.current?.offsetWidth ?? 160;
    const menuHeight = menuRef.current?.offsetHeight ?? 124;
    const viewportGap = 16;

    let left = rect.right + 12;
    if (left + menuWidth > window.innerWidth - viewportGap) {
      left = rect.left + rect.width / 2 - menuWidth / 2;
    }
    left = Math.max(viewportGap, Math.min(left, window.innerWidth - menuWidth - viewportGap));

    const top = Math.max(
      viewportGap,
      Math.min(rect.bottom - menuHeight, window.innerHeight - menuHeight - viewportGap),
    );

    setPos((value) => (value.top === top && value.left === left ? value : { top, left }));
  }, []);

  useLayoutEffect(() => {
    if (open) placeMenu();
  }, [open, placeMenu]);

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', placeMenu);
    window.addEventListener('scroll', placeMenu, true);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', placeMenu);
      window.removeEventListener('scroll', placeMenu, true);
    };
  }, [open, placeMenu]);

  return (
    <div className={cn('relative', containerClassName)}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label="Alterar tema"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Alterar tema"
        className={cn(
          'flex size-10 cursor-pointer items-center justify-center rounded-lg transition-colors',
          className,
        )}
      >
        <CurrentIcon size={20} />
      </button>

      {open
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              aria-label="Tema"
              style={{ top: pos.top, left: pos.left }}
              className={cn(
                'fixed z-50 w-40 rounded-xl border border-line bg-surface p-1 shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-1px_rgba(0,0,0,0.06)]',
                menuClassName,
              )}
              onMouseDown={(event) => event.stopPropagation()}
            >
              {THEME_OPTIONS.map((option) => {
                const Icon = option.icon;
                const selected = theme === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="menuitemradio"
                    aria-checked={selected}
                    onClick={() => {
                      setTheme(option.value);
                      setOpen(false);
                    }}
                    className={cn(
                      'flex h-9 w-full cursor-pointer items-center gap-2 rounded-lg px-2 text-sm text-ink hover:bg-hover',
                      selected && 'font-semibold text-brand-accent',
                    )}
                  >
                    <Icon size={16} className="shrink-0" />
                    <span className="min-w-0 flex-1 text-left">{option.label}</span>
                    {selected ? <Check size={16} className="shrink-0" /> : null}
                  </button>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
